import { Dots } from '@/app/components/Dots/Dots'
import { Motion } from '@/app/components/Motion/Motion'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useChat } from '@/app/state/chat'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import Markdown from 'react-markdown'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const MessageUI:
  & FunctionalComponent<{
    type: 'model' | 'user'
    loading?: boolean
    text?: string
    class?: string
    onClick?: () => void
  }>
  & {
    List: FunctionalComponent
    Reading: FunctionalComponent<{
      id: string
    }>
    Container: FunctionalComponent<{
      hidden?: boolean
      id?: string
    }>
    Head: FunctionalComponent
    Title: FunctionalComponent
    SubTitle: FunctionalComponent
    Body: FunctionalComponent
    Hint: FunctionalComponent
    Content: FunctionalComponent<{
      class?: string
    }>
  } = props => {
    return (
      <div
        onClick={props.onClick}
        class={classNames(styles.root, styles[props.type], props.class)}
      >
        <UI.Panel
          shadow={props.type === 'user'}
          type={props.type === 'user' ? 'user' : 'message'}
        >
          {props.children
            ? props.children
            : (
              <Text
                class={styles.text}
                fontSize={16}
                lineHeight={1.3}
              >
                {props.text && (
                  <Markdown
                    skipHtml
                    unwrapDisallowed
                    disallowedElements={[
                      'h1',
                      'h2',
                      'h3',
                      'h4',
                      'h5',
                      'h6',
                      'blockquote',
                      'table',
                      'pre',
                      'code',
                    ]}
                  >
                    {props.text}
                  </Markdown>
                )}

                {props.loading && <Dots />}
              </Text>
            )}
        </UI.Panel>
      </div>
    )
  }

MessageUI.List = props => (
  <div class={styles.list}>
    {props.children}
  </div>
)

MessageUI.Container = props => (
  <Motion
    class={classNames(styles.container, {
      [styles.hidden]: props.hidden,
    })}
    id={props.id}
  >
    {props.children}
  </Motion>
)

MessageUI.Reading = props => {
  const reading = useChat(useShallow(s => {
    const m = s.data[props.id]
    if (!m) return null
    return m.message.reading
  }))

  const count = reading?.cards.length

  if (!reading) return null

  return (
    <div class={classNames(styles.root, styles.reading)}>
      <div class={classNames(styles.cards, styles[`cards${count}`])}>
        {reading?.cards.map(item => (
          <img
            alt=''
            style={item.reversed ? { transform: 'rotate(180deg)' } : undefined}
            src={`/tarot/${item.index}.jpg`}
            class={item.reversed ? styles.reversed : undefined}
          />
        ))}
      </div>
    </div>
  )
}

MessageUI.Head = props => (
  <div class={styles.head}>
    {props.children}
  </div>
)

MessageUI.Title = props => (
  <div class={styles.title}>
    {props.children}
  </div>
)

MessageUI.SubTitle = props => (
  <div class={styles.subTitle}>
    {props.children}
  </div>
)

MessageUI.Body = props => (
  <div class={styles.body}>
    {props.children}
  </div>
)

MessageUI.Hint = props => (
  <div class={styles.hint}>
    {props.children}
  </div>
)

MessageUI.Content = props => (
  <div class={classNames(styles.content, props.class)}>
    {props.children}
  </div>
)
